import type { RiskLabel, ScoreRange } from '../types'
import { DEFAULT_RISK_COLORS } from '../constants'

export { DEFAULT_RISK_COLORS } from '../constants'

export function to01(value: unknown): number {
  if (value === true) return 1
  if (value === false) return 0
  const n = Number(value)
  return n ? 1 : 0
}

export function getRangeKey(range: ScoreRange, index: number): number {
  if (range?.id != null) return range.id
  if (range?.tmp_key != null) return range.tmp_key
  return index
}

export function scoreRangesOverlap(a: ScoreRange, b: ScoreRange): boolean {
  const amin = Number(a.min_value)
  const amax = Number(a.max_value)
  const bmin = Number(b.min_value)
  const bmax = Number(b.max_value)
  return !(amax < bmin || bmax < amin)
}

export function scoreRangesIdentical(a: ScoreRange, b: ScoreRange): boolean {
  return Number(a.min_value) === Number(b.min_value) && Number(a.max_value) === Number(b.max_value)
}

export function validateScoreRanges(
  ranges: ScoreRange[],
  kind: 'Eq' | 'Ninja',
  applicantType?: 'single' | 'multiple',
): string | null {
  const label = kind === 'Eq' ? 'Eq score' : 'Ninja score'
  const tab =
    applicantType === 'single' ? ' (Single Apps)' : applicantType === 'multiple' ? ' (Multiple Apps)' : ''
  const list = ranges || []
  for (let i = 0; i < list.length; i++) {
    const r = list[i]
    const lo = Number(r.min_value)
    const hi = Number(r.max_value)
    if (Number.isNaN(lo) || Number.isNaN(hi)) {
      return `${label} range #${i + 1}${tab}: min and max must be valid numbers.`
    }
    if (lo > hi) {
      return `${label} range "${lo}-${hi}"${tab}: min value cannot be greater than max value.`
    }
  }
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]
      const b = list[j]
      if (scoreRangesIdentical(a, b)) {
        return `${label} ranges "${a.min_value}-${a.max_value}" and "${b.min_value}-${b.max_value}" are duplicates${tab}.`
      }
      if (scoreRangesOverlap(a, b)) {
        return `${label} ranges "${a.min_value}-${a.max_value}" and "${b.min_value}-${b.max_value}" overlap${tab}.`
      }
    }
  }
  return null
}

export function formatRiskLabelText(text: string, trimTrailing = false): string {
  const value = (text || '').replace(/^\s+/, '').toUpperCase()
  return trimTrailing ? value.trim() : value
}

export function normalizeRiskLabelText(text: string): string {
  return formatRiskLabelText(text, true)
}

export function mapRiskLabel(lbl: Partial<RiskLabel>, idx: number, applicantType: string): RiskLabel {
  return {
    id: lbl.id,
    numeric_value: Number(lbl.numeric_value),
    label: normalizeRiskLabelText(String(lbl.label || '')),
    color: lbl.color || DEFAULT_RISK_COLORS[idx % DEFAULT_RISK_COLORS.length],
    access_fee_percentage: Number(lbl.access_fee_percentage) || 0,
    applicant_type: applicantType,
  }
}

export function hasDuplicateRiskLevelNumbers(labels: RiskLabel[]): boolean {
  const seen = new Set<number>()
  for (let i = 0; i < (labels || []).length; i++) {
    const v = Number(labels[i].numeric_value)
    if (seen.has(v)) return true
    seen.add(v)
  }
  return false
}

export function isRiskLevelNumberTaken(
  labels: RiskLabel[],
  value: number,
  except?: RiskLabel,
): boolean {
  const v = Number(value)
  for (let i = 0; i < (labels || []).length; i++) {
    const lbl = labels[i]
    if (except && lbl === except) continue
    if (Number(lbl.numeric_value) === v) return true
  }
  return false
}

export function validateRiskLabels(
  labels: RiskLabel[],
  applicantType: 'single' | 'multiple',
): string | null {
  const tab = applicantType === 'single' ? 'Single Apps' : 'Multiple Apps'
  for (let i = 0; i < (labels || []).length; i++) {
    const label = labels[i]
    if (!label) continue
    const normalized = normalizeRiskLabelText(label.label)
    if (!normalized) {
      return `Risk label is required for level ${label.numeric_value} (${tab}).`
    }
  }
  return null
}

export function hexToLightBg(hex: string): string {
  if (!hex || hex.length < 7) return '#ffffff'
  let r = parseInt(hex.slice(1, 3), 16)
  let g = parseInt(hex.slice(3, 5), 16)
  let b = parseInt(hex.slice(5, 7), 16)
  r = Math.round(r * 0.15 + 255 * 0.85)
  g = Math.round(g * 0.15 + 255 * 0.85)
  b = Math.round(b * 0.15 + 255 * 0.85)
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

export function getRiskCellTextColor(hex: string | undefined): string {
  if (!hex) return '#333'
  const normalized = normalizeHex(hex)
  if (!normalized) return '#333'
  const r = parseInt(normalized.slice(1, 3), 16)
  const g = parseInt(normalized.slice(3, 5), 16)
  const b = parseInt(normalized.slice(5, 7), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#333' : '#fff'
}

function normalizeHex(hex: string): string | null {
  const raw = hex.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[1]
    const g = raw[2]
    const b = raw[3]
    return `#${r}${r}${g}${g}${b}${b}`
  }
  return null
}

export function findRiskLabel(labels: RiskLabel[], value: number): RiskLabel | undefined {
  for (let i = 0; i < labels.length; i++) {
    if (labels[i].numeric_value === value) return labels[i]
  }
  return undefined
}

export function getRangeLabel(range: ScoreRange): string {
  if (range.min_value === 0 && range.max_value === 0) return '0'
  if (range.max_value >= 9999) return `${range.min_value}+`
  return `${range.min_value}-${range.max_value}`
}

export function buildMatrixPayload(
  _type: 'single' | 'multiple',
  matrix: Record<string, number>,
  ninjaRanges: ScoreRange[],
  equifaxRanges: ScoreRange[],
  fallbackLevel: number,
): {
  ninja_range_index: number
  equifax_range_index: number
  ninja_range_id: number | null
  equifax_range_id: number | null
  numeric_value: number
}[] {
  const values: {
    ninja_range_index: number
    equifax_range_index: number
    ninja_range_id: number | null
    equifax_range_id: number | null
    numeric_value: number
  }[] = []
  ninjaRanges.forEach((nr, ni) => {
    equifaxRanges.forEach((er, ei) => {
      const ninjaKey = getRangeKey(nr, ni)
      const equifaxKey = getRangeKey(er, ei)
      const key = `${ninjaKey}_${equifaxKey}`
      const raw = matrix[key]
      const numeric =
        raw !== null && raw !== undefined ? raw : fallbackLevel
      values.push({
        ninja_range_index: ni,
        equifax_range_index: ei,
        ninja_range_id: nr.id ?? null,
        equifax_range_id: er.id ?? null,
        numeric_value: numeric,
      })
    })
  })
  return values
}

export function sameNumberSet(a: number[], b: number[]): boolean {
  const as = [...(a || [])].map(Number).sort((x, y) => x - y)
  const bs = [...(b || [])].map(Number).sort((x, y) => x - y)
  if (as.length !== bs.length) return false
  for (let i = 0; i < as.length; i++) {
    if (as[i] !== bs[i]) return false
  }
  return true
}
